import {
  VegaAccordion,
  VegaBox,
  VegaCard,
  VegaChip,
  VegaCodeBlock,
  VegaDivider,
  VegaFlex,
  VegaFont,
  VegaGrid,
} from '@globalpayments/vega-react';

import { format } from 'date-fns';
import { useState } from 'react';

import { getDate } from '../../configInfo/components/Functions';
import { formatCodeBlock } from '../../../utility/formatCodeBlock';

import { UserClientCardProps } from './UserClientCardProps';

interface Props {
  cardProps: UserClientCardProps;
}

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <VegaFlex direction="col" gap="size-4">
    <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
      {label}
    </VegaFont>
    <VegaFont as="span" variant="font-p2-short">
      {value || 'N/A'}
    </VegaFont>
  </VegaFlex>
);

const UserClientCard: React.FC<Props> = ({ cardProps }) => {
  const dateCreated = format(getDate(new Date(cardProps.createdUTC)), 'MM/dd/yyyy, hh:mm:ssaaa');
  const dateLastUpdate = format(getDate(new Date(cardProps.lastUpdatedUTC)), 'MM/dd/yyyy, hh:mm:ssaaa');
  const [showAttributions, setShowAttributions] = useState(false);
  const [showApplications, setShowApplications] = useState(false);

  return (
    <VegaCard data-testid="user-client-result-card" padding="size-16" variant="border">
      <VegaFlex direction="col" gap="size-16">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-12" justifyContent="space-between">
          <VegaFlex direction="col" gap="size-4">
            <VegaFont as="h3" variant="font-h5">
              {cardProps.email || 'Email unavailable'}
            </VegaFont>
            <VegaFont as="span" color="text-secondary" variant="font-p2-short">
              {cardProps.userClientUId}
            </VegaFont>
          </VegaFlex>
          <VegaFlex flexWrap="wrap" gap="size-8">
            <VegaChip
              chipType="status"
              size="extra-small"
              statusColor={cardProps.isEmailVerified ? 'bg-status-success' : 'bg-status-warning'}
              text={cardProps.isEmailVerified ? 'Email verified' : 'Email unverified'}
            />
            {cardProps.isPartner && <VegaChip chipType="label" size="extra-small" text="Partner" variant="outlined" />}
          </VegaFlex>
        </VegaFlex>
        <VegaDivider />
        <VegaGrid column={{ default: 1, M: 2, L: 3 }} gap="size-16">
          <Detail label="IP address" value={cardProps.ipAddress} />
          <Detail label="Created" value={dateCreated} />
          <Detail label="Last updated" value={dateLastUpdate} />
        </VegaGrid>
        <Detail label="User agent" value={cardProps.userAgent} />

        <VegaAccordion
          accordionTitle={`Attributions (${cardProps.userClientAttributions.length})`}
          expand={showAttributions}
          expandIconAlign="right"
          onVegaExpand={(event) => setShowAttributions(event.detail)}
          showExpandIcon>
          <VegaBox padding={{ top: 'size-16' }} slot="content">
            {cardProps.userClientAttributions.length > 0 ? (
              <VegaFlex direction="col" gap="size-16">
                {cardProps.userClientAttributions.map((attribution, index) => (
                  <VegaFlex
                    direction="col"
                    gap="size-12"
                    key={`${attribution.type}-${attribution.identifier}-${index}`}>
                    <VegaGrid column={{ default: 1, M: 3 }} gap="size-16">
                      <Detail label="Type" value={attribution.type} />
                      <Detail label="Identifier" value={attribution.identifier} />
                      <Detail label="Display value" value={attribution.displayValue} />
                    </VegaGrid>
                    {index < cardProps.userClientAttributions.length - 1 && <VegaDivider />}
                  </VegaFlex>
                ))}
              </VegaFlex>
            ) : (
              <VegaFont as="p" color="text-secondary" variant="font-p2-short">
                No attributions
              </VegaFont>
            )}
          </VegaBox>
        </VegaAccordion>

        <VegaAccordion
          accordionTitle={`Applications (${cardProps.userClientSessions.length})`}
          expand={showApplications}
          expandIconAlign="right"
          onVegaExpand={(event) => setShowApplications(event.detail)}
          showExpandIcon>
          <VegaBox padding={{ top: 'size-16' }} slot="content">
            {cardProps.userClientSessions.length > 0 ? (
              <VegaFlex direction="col" gap="size-16">
                {cardProps.userClientSessions.map((session, index) => (
                  <VegaFlex direction="col" gap="size-12" key={`${session.appUid}-${index}`}>
                    <VegaFlex alignItems="center" flexWrap="wrap" gap="size-8" justifyContent="space-between">
                      <VegaFont as="h4" variant="font-h6">
                        {session.configName || 'Application'}
                      </VegaFont>
                      <VegaFlex flexWrap="wrap" gap="size-8">
                        <VegaChip
                          chipType="status"
                          size="extra-small"
                          statusColor={session.isCompleted ? 'bg-status-success' : 'bg-status-info'}
                          text={session.isCompleted ? 'Completed' : 'In progress'}
                        />
                        {session.isPartner && (
                          <VegaChip chipType="label" size="extra-small" text="Partner" variant="outlined" />
                        )}
                      </VegaFlex>
                    </VegaFlex>
                    <VegaGrid column={{ default: 1, M: 2 }} gap="size-16">
                      <Detail label="Application UID" value={session.appUid} />
                      <Detail label="Eligibility" value={session.isDisqualified ? 'Disqualified' : 'Qualified'} />
                      <Detail label="Marketing preference" value={session.isOptedOut ? 'Opted out' : 'Opted in'} />
                      <Detail label="Lead conversion" value={session.isLeadConverted ? 'Converted' : 'Not converted'} />
                    </VegaGrid>
                    <VegaCodeBlock readOnly showCopyButton value={formatCodeBlock(session.companyNameJson || 'N/A')} />
                    {index < cardProps.userClientSessions.length - 1 && <VegaDivider />}
                  </VegaFlex>
                ))}
              </VegaFlex>
            ) : (
              <VegaFont as="p" color="text-secondary" variant="font-p2-short">
                No applications
              </VegaFont>
            )}
          </VegaBox>
        </VegaAccordion>
      </VegaFlex>
    </VegaCard>
  );
};

export default UserClientCard;
